import { useEffect, useRef, useState } from 'react';

const AboutSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: '500+', label: 'Projects Completed', icon: 'ri-award-line' },
    { value: '20+', label: 'Years of Experience', icon: 'ri-time-line' },
    { value: '30mi', label: 'Service Radius', icon: 'ri-map-pin-2-line' },
    { value: '100%', label: 'Licensed & Insured', icon: 'ri-shield-check-line' },
  ];

  return (
    <section id="about" ref={ref} className="py-20 md:py-28 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Text Content */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-brand-teal/40 rounded-full mb-5">
              <div className="w-1.5 h-1.5 bg-brand-teal rounded-full" />
              <span className="text-xs font-semibold text-brand-teal uppercase tracking-widest">About Valley View</span>
            </div>

            <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark leading-tight mb-6">
              Building Michiana's
              <span className="block text-brand-teal italic">Legacy, One Stone</span>
              <span className="block">at a Time</span>
            </h2>

            <p className="text-base text-brand-stone leading-relaxed mb-5">
              Valley View Construction is Elkhart, Indiana's trusted name in masonry and construction. For over two decades, our craftsmen have been transforming residential and commercial properties throughout the Michiana area with exceptional stonework, hardscape artistry, and full construction services.
            </p>
            <p className="text-base text-brand-stone leading-relaxed mb-8">
              From hand-laid stone patios and outdoor kitchens to retaining walls, chimney construction, fencing, and complete remodeling, we bring the same masonry-level precision to every project. Licensed, bonded, and fully insured — serving all clients within 30 miles of Elkhart.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {['Licensed & Bonded', 'Free Consultations', 'Commercial & Residential', 'All Michiana Served'].map((badge) => (
                <span key={badge} className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-dark rounded-lg text-xs font-medium text-white">
                  <i className="ri-checkbox-circle-fill text-brand-teal-light text-xs" />
                  {badge}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+15744096118"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-teal hover:bg-brand-teal-light text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </div>
                Call 574-409-6118
              </a>
              <a
                href="mailto:valleyviewconstruction98@gmail.com"
                className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </div>
                Email Us
              </a>
            </div>
          </div>

          {/* Right: Images & Stats */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {/* Image Stack */}
            <div className="relative mb-8">
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/7abff2709c612645e6c085daf68dd7d3.jpg"
                  alt="Valley View Construction team working on masonry project in Elkhart Indiana"
                  className="w-full h-64 md:h-80 object-cover object-top rounded-2xl"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-5 -left-4 bg-brand-dark rounded-xl px-5 py-4 border border-brand-teal/20">
                  <div className="text-3xl font-bold font-serif text-brand-teal-light">20+</div>
                  <div className="text-xs text-white/60 font-medium uppercase tracking-wide mt-0.5">Years in Michiana</div>
                </div>
              </div>

              <div className="relative mt-4 pl-16">
                <img
                  src="https://readdy.ai/api/search-image?query=beautiful%20completed%20stone%20patio%20project%20with%20outdoor%20furniture%2C%20professional%20masonry%20installation%20residential%20indiana%2C%20warm%20sunset%20lighting%2C%20premium%20craftsmanship%20result%2C%20stone%20work%20detail&width=600&height=350&seq=about002&orientation=landscape"
                  alt="Completed masonry patio project by Valley View Construction"
                  className="w-full h-48 md:h-56 object-cover object-top rounded-2xl"
                />
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-4 flex items-center gap-3 border border-brand-cream-dark">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-teal/10 rounded-lg flex-shrink-0">
                    <i className={`${stat.icon} text-brand-teal text-lg`} />
                  </div>
                  <div>
                    <div className="text-xl font-bold font-serif text-brand-dark">{stat.value}</div>
                    <div className="text-xs text-brand-stone leading-tight">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
